import { useMutation } from '@apollo/client';
import type { DeleteApprovalMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteApprovalDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteApprovalTRPC } from './useDeleteApprovalTRPC';

export const useDeleteApproval = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [deleteApprovalGraphQL, graphqlState] = useMutation(
    DeleteApprovalDocument,
    {
      update: (cache) => evictField(cache, 'approval'),
    }
  );

  // tRPC mutation
  const trpcMutation = useDeleteApprovalTRPC();

  const deleteApproval = async (variables: {
    Id: string;
  }): Promise<DeleteApprovalMutation> => {
    if (trpcEnabled) {
      return trpcMutation.deleteApproval(variables);
    }

    const result = await deleteApprovalGraphQL({
      variables: { Id: variables.Id },
    });
    if (!result.data) {
      throw new Error('Failed to delete approval');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      deleteApproval,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    deleteApproval,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
